/** Centralized Stimp-style green speed calibration.
 *
 * We map a player-facing Stimp value to kinetic deceleration with an empirical
 * inverse relationship anchored at Stimp 10 = 0.64 m/s². On flat greens this
 * yields documented roll distances: 1 m/s ≈ 0.78 m, 2 m/s ≈ 3.13 m,
 * 3 m/s ≈ 7.03 m, and 4 m/s ≈ 12.5 m.
 */
export interface GreenSpeedProfile {
  readonly name: string;
  readonly stimpFeet: number;
  readonly kineticDecelerationMetersPerSecondSquared: number;
  readonly staticHoldAcceleration: number;
  readonly stopSpeedMetersPerSecond: number;
  readonly firmness: number;
}

export const REFERENCE_STIMP_FEET = 10;
export const REFERENCE_GREEN_DECELERATION = 0.64;

export function greenSpeedProfile(
  name: string,
  stimpFeet: number,
  firmness = 1,
): GreenSpeedProfile {
  const stimp = Math.max(6, stimpFeet);
  const deceleration = REFERENCE_GREEN_DECELERATION * (REFERENCE_STIMP_FEET / stimp);
  return {
    name,
    stimpFeet: stimp,
    kineticDecelerationMetersPerSecondSquared: deceleration,
    staticHoldAcceleration: deceleration * 0.82,
    stopSpeedMetersPerSecond: 0.11,
    firmness,
  };
}

export const GREEN_SPEED_PROFILES = {
  slow: greenSpeedProfile('Slow', 8.5, 0.9),
  medium: greenSpeedProfile('Medium', 10, 1),
  fast: greenSpeedProfile('Fast', 11.5, 1.05),
  tournament: greenSpeedProfile('Tournament', 12.5, 1.1),
} as const;

export const DEFAULT_GREEN_SPEED_PROFILE = GREEN_SPEED_PROFILES.medium;
